// The Space's online presence — the quiet line of links under the Space's title, its ONE next-step pill, the
// suggested-website line, and the Manage dialog's sections. It replaced the Space "site bar".
//
// Every decision is here, pure and unit-tested (`test/presence.test.ts`); the components
// (components/symphony/website/PresenceLine.tsx, PresenceManage.tsx) only draw it. The SAME rules, under the same
// names and the same assertions, live on both phones (iOS `Presence.swift`, Android `Presence.kt`) — change one,
// change all three.
//
// Inputs: the Space's PRESENT website tile (`presentWebsite(workspace.website)`, which still decides the main
// site's publish state, plan and domain) and symphony-bff's ListSpaceBindings answer. Enum values this build has
// never heard of are dropped or read as unknown, never guessed at.

import {
  providerName,
  spaceResourceRow,
  type I18nCopy,
  type SpaceBindingsResponse,
  type SpaceResource,
  type SpaceResourceAction,
  type SpaceResourceKind,
  type StateBadgeTone,
} from './space-resources';
import { websiteDisplayName, websiteHost, websitePublishPrompt, type WorkspaceWebsite } from './workspace-website';

const K = 'workspace.presence.';
const R = 'workspace.resources.';

/* ------------------------------------------------------------------------------------------------ glyphs */

/** The mark a provider is drawn with. Every surface maps these ids to its own `SocialLogo`; `globe` is the fallback. */
export type PresenceGlyph =
  | 'globe'
  | 'facebook'
  | 'instagram'
  | 'x'
  | 'youtube'
  | 'tiktok'
  | 'linkedin'
  | 'pinterest'
  | 'threads'
  | 'google'
  | 'yelp'
  | 'etsy'
  | 'shopify';

const GLYPHS: Record<string, PresenceGlyph> = {
  FACEBOOK: 'facebook',
  INSTAGRAM: 'instagram',
  X: 'x',
  YOUTUBE: 'youtube',
  TIKTOK: 'tiktok',
  LINKEDIN: 'linkedin',
  PINTEREST: 'pinterest',
  THREADS: 'threads',
  GOOGLE_BUSINESS: 'google',
  YELP: 'yelp',
  ETSY: 'etsy',
  SHOPIFY: 'shopify',
};

/** The glyph for a provider. WIX, WORDPRESS, URL, WIXEL and anything new draw the generic globe. */
export function linkProviderGlyph(provider: string | undefined): PresenceGlyph {
  return GLYPHS[provider ?? ''] ?? 'globe';
}

/**
 * The provider's own name, for an icon's accessible name and a row's detail line. Brand names are proper nouns and
 * are not translated; a plain link (URL) or an unknown provider has none (empty), and callers fall back to
 * `workspace.resources.kindWebsite` for a website.
 */
export function linkProviderName(provider: string | undefined): string {
  if (!provider || provider === 'URL') return '';
  const name = providerName(provider);
  if (name === 'account' || name === 'website') return '';
  // `providerName` says "Wix site" (a chat seed); the detail line wants the brand.
  return provider === 'WIX' ? 'Wix' : name;
}

/* --------------------------------------------------------------------------------------- shared helpers */

/** The listings providers: drawn as links, grouped under "Listings and reviews" in Manage. */
const LISTING_PROVIDERS = new Set(['GOOGLE_BUSINESS', 'YELP']);

const KNOWN_KINDS = new Set<SpaceResourceKind>(['WEBSITE', 'BUSINESS_DATA', 'BRAND', 'SOCIAL_PROFILE']);

/** The order the header draws other presences in, after the websites; anything else keeps the server's order after these. */
const PROVIDER_RANK: Record<string, number> = { FACEBOOK: 0, INSTAGRAM: 1, X: 2, GOOGLE_BUSINESS: 3, YOUTUBE: 4 };

function rank(provider: string | undefined): number {
  return PROVIDER_RANK[provider ?? ''] ?? 5;
}

/** Only an http(s) address is ever a link; anything else (empty, `javascript:`, garbage) is no link. */
export function presenceHref(url: string | undefined): string {
  const raw = (url ?? '').trim();
  if (!raw) return '';
  try {
    const parsed = new URL(raw);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? raw : '';
  } catch {
    return '';
  }
}

function rowName(r: SpaceResource): string {
  return (r.displayName ?? '').trim() || websiteHost(presenceHref(r.openUrl)) || (r.externalScopeId ?? '').trim();
}

/** Rows that are resources (not suggestions) of a kind this build knows. */
function settled(bindings: SpaceBindingsResponse | undefined): SpaceResource[] {
  return (bindings?.resources ?? []).filter((r) => r.state !== 'CANDIDATE' && KNOWN_KINDS.has(r.kind as SpaceResourceKind));
}

function mainFirst(rows: SpaceResource[]): SpaceResource[] {
  return [...rows.filter((r) => r.main === true), ...rows.filter((r) => r.main !== true)];
}

/** A paid plan: `premium`, or a custom domain (a custom domain needs a paid plan, whatever `premium` reads). */
function sitePaid(website: WorkspaceWebsite): boolean {
  return website.premium === true || !!(website.customDomain ?? '').trim();
}

/**
 * The WEBSITE row the tile stands for, when there is a tile: the main WEBSITE row, or the one on the tile's metasite
 * or host. It is never drawn twice.
 */
function tileRow(website: WorkspaceWebsite | null, resources: SpaceResource[]): SpaceResource | undefined {
  if (!website) return undefined;
  const sites = resources.filter((r) => r.kind === 'WEBSITE');
  const msid = (website.metaSiteId ?? '').trim();
  const host = websiteHost((website.url ?? '').trim());
  return (
    sites.find((r) => r.main === true) ??
    sites.find((r) => msid && (r.externalScopeId ?? '').trim() === msid) ??
    sites.find((r) => host && websiteHost(presenceHref(r.openUrl)) === host)
  );
}

/* -------------------------------------------------------------------------------------------- attention */

/** Why a presence wears a dot. LINK_ONLY, CONNECTED and an unknown state never do. */
export type PresenceAttention = 'NOT_LIVE' | 'RECONNECT' | 'NOT_VERIFIED' | 'NOT_OWNED';

/** The attention's words (for an icon's accessible name, and a row's state). */
export const ATTENTION_KEYS: Record<PresenceAttention, string> = {
  NOT_LIVE: 'workspace.website.notLive',
  RECONNECT: `${R}stateBroken`,
  NOT_VERIFIED: `${K}stateNotVerified`,
  NOT_OWNED: `${R}stateNotOwned`,
};

/**
 * A row's attention: BROKEN → RECONNECT; NOT_OWNED → NOT_OWNED; UNPUBLISHED → NOT_VERIFIED on a Google listing
 * (an unverified Business Profile is not public yet), NOT_LIVE on anything else. Everything else, LINK_ONLY
 * included, has none.
 */
export function resourceAttention(r: Pick<SpaceResource, 'state' | 'provider'>): PresenceAttention | undefined {
  switch (r.state) {
    case 'BROKEN':
      return 'RECONNECT';
    case 'NOT_OWNED':
      return 'NOT_OWNED';
    case 'UNPUBLISHED':
      return r.provider === 'GOOGLE_BUSINESS' ? 'NOT_VERIFIED' : 'NOT_LIVE';
    default:
      return undefined;
  }
}

/* ---------------------------------------------------------------------------------------- the line */

export interface PresenceLink {
  /** Stable React key: the binding id, or `website` for the tile's site. */
  id: string;
  /** The binding id; empty for the tile's site when no binding stands for it. */
  bindingId: string;
  provider: string;
  glyph: PresenceGlyph;
  /** The main website: drawn as a globe plus `name` (its host), not a round icon. Only the first link can be main. */
  main: boolean;
  /** The main website's host; the others' display name. */
  name: string;
  /** The brand (`linkProviderName`), empty for a plain link. */
  providerName: string;
  /** The public page; empty = nothing public to open (a site not live yet), so a tap opens Manage instead. */
  url: string;
  attention?: PresenceAttention;
}

function linkOf(r: SpaceResource): PresenceLink | null {
  const name = rowName(r);
  const url = presenceHref(r.openUrl);
  if (!name && !url) return null;
  return {
    id: r.bindingId ?? name,
    bindingId: r.bindingId ?? '',
    provider: r.provider ?? '',
    glyph: linkProviderGlyph(r.provider),
    main: false,
    name,
    providerName: linkProviderName(r.provider),
    url,
    attention: resourceAttention(r),
  };
}

/**
 * The header line, in order:
 *   1. the main website — the tile's site when there is one (its host, a dot when `published === false`, no link
 *      until it is live), else the main WEBSITE row;
 *   2. the other websites, in the server's order;
 *   3. the social profiles and listings: Facebook, Instagram, X, Google Business, YouTube, then the rest in the
 *      server's order.
 * CANDIDATE rows, BUSINESS_DATA and BRAND rows, unknown kinds, and rows with neither a name nor a link are never
 * drawn. A row the tile stands for is not drawn twice.
 */
export function presenceLinks(website: WorkspaceWebsite | null | undefined, bindings: SpaceBindingsResponse | undefined): PresenceLink[] {
  const site = website ?? null;
  const resources = settled(bindings);
  const links: PresenceLink[] = [];
  const standIn = tileRow(site, resources);
  let mainRow: SpaceResource | undefined;

  if (site) {
    const url = (site.url ?? '').trim();
    const live = site.published !== false;
    links.push({
      id: 'website',
      bindingId: standIn?.bindingId ?? '',
      provider: standIn?.provider || 'WIX',
      glyph: 'globe',
      main: true,
      name: websiteHost(url) || websiteDisplayName(site),
      providerName: linkProviderName(standIn?.provider || 'WIX'),
      url: live ? presenceHref(url) : '',
      attention: live ? undefined : 'NOT_LIVE',
    });
  } else {
    mainRow = resources.find((r) => r.kind === 'WEBSITE' && r.main === true);
    const main = mainRow ? linkOf(mainRow) : null;
    if (main) links.push({ ...main, main: true, glyph: 'globe' });
  }

  const skip = standIn ?? mainRow;
  for (const r of resources) {
    if (r.kind !== 'WEBSITE' || r === skip) continue;
    const link = linkOf(r);
    if (link) links.push(link);
  }
  const others = resources
    .map((r, i) => ({ r, i }))
    .filter(({ r }) => r.kind === 'SOCIAL_PROFILE')
    .sort((a, b) => rank(a.r.provider) - rank(b.r.provider) || a.i - b.i);
  for (const { r } of others) {
    const link = linkOf(r);
    if (link) links.push(link);
  }
  return links;
}

/** A suggestion: a row in state CANDIDATE, which the owner confirms (it becomes the Space's) or dismisses. */
export interface PresenceCandidate {
  bindingId: string;
  kind: SpaceResourceKind;
  provider: string;
  glyph: PresenceGlyph;
  /** `display_name`, else the host of its `open_url`, else `external_scope_id`. */
  name: string;
  openUrl: string;
}

/** Every suggestion of a known kind that has a binding id to answer and a name to ask about, in the server's order. */
export function presenceCandidates(bindings: SpaceBindingsResponse | undefined): PresenceCandidate[] {
  return (bindings?.resources ?? [])
    .filter((r) => r.state === 'CANDIDATE' && KNOWN_KINDS.has(r.kind as SpaceResourceKind) && (r.bindingId ?? '').trim())
    .map((r) => ({
      bindingId: (r.bindingId ?? '').trim(),
      kind: r.kind as SpaceResourceKind,
      provider: r.provider ?? '',
      glyph: linkProviderGlyph(r.provider),
      name: rowName(r),
      openUrl: presenceHref(r.openUrl),
    }))
    .filter((c) => c.name);
}

/**
 * The suggested website the header asks about ("Is {name} this Space's website?"): the first WEBSITE suggestion, and
 * only while the Space has no main website. Null otherwise.
 */
export function presenceCandidate(website: WorkspaceWebsite | null | undefined, bindings: SpaceBindingsResponse | undefined): PresenceCandidate | null {
  if (website) return null;
  if (settled(bindings).some((r) => r.kind === 'WEBSITE' && r.main === true)) return null;
  return presenceCandidates(bindings).find((c) => c.kind === 'WEBSITE') ?? null;
}

/**
 * Whether the Space shows anything at all: a presence link, or a suggested website. Personal, and any Space with no
 * website, profile or suggestion, shows nothing — no line, no pill, no Manage.
 */
export function presenceVisible(website: WorkspaceWebsite | null | undefined, bindings: SpaceBindingsResponse | undefined): boolean {
  return presenceLinks(website, bindings).length > 0 || presenceCandidate(website, bindings) !== null;
}

/* ------------------------------------------------------------------------------------------ chat handoffs */

/** What a resource chat is about; each has its own seed and its own clause in the hidden context. */
export type PresenceIntent = 'RECONNECT' | 'VERIFY' | 'CONNECT' | 'CHANGE';

/**
 * A chat an action opens. The visible seed is a catalog key (the website prompts) or, for a resource, an English
 * sentence to the agent — the same on all three surfaces, like `reconnectPrompt`. The hidden context is built at the
 * call site, which knows the Space: `websiteChatContext(workspaceId, name, website)` for `website`, and
 * `presenceChatContext(workspaceId, name, resource, intent)` for `resource`.
 */
export type PresenceChat =
  | { message: I18nCopy; context: { kind: 'website'; website: WorkspaceWebsite } }
  | { text: string; context: { kind: 'resource'; intent: PresenceIntent; resource: PresenceChatResource } }
  /** A checklist step on a Space with no website yet (BUILD): there is no site to name, only the Space. */
  | { message: I18nCopy; context: { kind: 'space' } };

export interface PresenceChatResource {
  bindingId: string;
  provider: string;
  name: string;
  kind: string;
}

function resourceOf(r: SpaceResource): PresenceChatResource {
  return { bindingId: r.bindingId ?? '', provider: r.provider ?? '', name: rowName(r), kind: r.kind ?? '' };
}

/** The English seed for a resource chat. RECONNECT is `reconnectPrompt`'s sentence, unchanged. */
export function presencePrompt(intent: PresenceIntent, resource: Pick<PresenceChatResource, 'provider' | 'kind'>): string {
  const name = providerName(resource.provider);
  switch (intent) {
    case 'RECONNECT':
      return `Reconnect my ${name} for this Space`;
    case 'VERIFY':
      return `Verify my ${name} for this Space`;
    case 'CONNECT':
      return `Connect my ${name} to this Space`;
    case 'CHANGE':
    default:
      return `Change the ${resource.kind === 'BRAND' ? 'brand' : 'business data'} this Space uses`;
  }
}

const CONTEXT_CLAUSES: Record<PresenceIntent, string> = {
  RECONNECT: 'whose login needs reconnecting.',
  VERIFY: "which isn't verified yet.",
  CONNECT: "which is only a public link today; connect it so agents can act on it.",
  CHANGE: 'which agents use for this business; the user wants to change it.',
};

/**
 * The hidden context a resource chat carries, so the agent works in this Space on this binding. Agent-facing, so
 * English; byte-for-byte the same on both phones. RECONNECT's is `spaceResourceChatContext`'s, unchanged.
 */
export function presenceChatContext(
  workspaceId: string,
  workspaceName: string,
  resource: Pick<PresenceChatResource, 'provider' | 'name' | 'bindingId'>,
  intent: PresenceIntent,
): string {
  return (
    `The user is asking about a resource of their "${workspaceName}" Space (workspace id "${workspaceId}"): ` +
    `their ${providerName(resource.provider)} "${resource.name}" (binding id "${resource.bindingId}"), ${CONTEXT_CLAUSES[intent]} ` +
    `Work in this Space. Don't ask which Space.`
  );
}

/**
 * The hidden context of a presence chat about the Space itself (a checklist step with no website to name yet), so the
 * agent works in this Space. Agent-facing, so English.
 */
export function presenceSpaceChatContext(workspaceId: string, workspaceName: string): string {
  return `The user is asking about the online presence of their "${workspaceName}" Space (workspace id "${workspaceId}"). Work in this Space. Don't ask which Space.`;
}

function websiteChat(key: string, url: string, website: WorkspaceWebsite): PresenceChat {
  return { message: { key, values: { url } }, context: { kind: 'website', website } };
}

function resourceChat(intent: PresenceIntent, r: SpaceResource): PresenceChat {
  const resource = resourceOf(r);
  return { text: presencePrompt(intent, resource), context: { kind: 'resource', intent, resource } };
}

/* -------------------------------------------------------------------------------------------- next step */

export type PresenceNextStepKind = 'GO_LIVE' | 'UPGRADE' | 'CONNECT_DOMAIN' | 'RECONNECT' | 'VERIFY';

export interface PresenceNextStep {
  kind: PresenceNextStepKind;
  /** The pill's sentence. */
  text: I18nCopy;
  /** The lime action's label. */
  actionKey: string;
  /** The page-click BI button name: `presence_next_step_<kind>`. */
  bi: string;
  chat: PresenceChat;
}

/**
 * The ONE most valuable next step, or null (no pill). In value order:
 *   1. the main website is not live (`published === false`) → GO_LIVE;
 *   2. it is on a free plan → UPGRADE;
 *   3. it is paid with no custom domain → CONNECT_DOMAIN;
 *   4. a social profile's login is broken (BROKEN) → RECONNECT, the first in the header's order;
 *   5. a Google listing is not verified (UNPUBLISHED) → VERIFY.
 * 1–3 read the website tile, and the seeds are the site bar's (publish / Premium / own domain). 4–5 open the Space's
 * chat seeded for that presence.
 */
export function presenceNextStep(website: WorkspaceWebsite | null | undefined, bindings: SpaceBindingsResponse | undefined): PresenceNextStep | null {
  const site = website ?? null;
  if (site) {
    const url = (site.url ?? '').trim();
    if (site.published === false) {
      const prompt = websitePublishPrompt(site);
      return {
        kind: 'GO_LIVE',
        text: { key: `${K}nextGoLive` },
        actionKey: 'workspace.website.goLiveNow',
        bi: 'presence_next_step_go_live',
        chat: { message: { key: prompt.key, values: prompt.values as Record<string, string> }, context: { kind: 'website', website: site } },
      };
    }
    if (!sitePaid(site)) {
      return {
        kind: 'UPGRADE',
        text: { key: `${K}nextUpgrade` },
        actionKey: 'workspace.website.upgrade',
        bi: 'presence_next_step_upgrade',
        chat: websiteChat('workspace.website.upgradePrompt', url, site),
      };
    }
    if (!(site.customDomain ?? '').trim()) {
      return {
        kind: 'CONNECT_DOMAIN',
        text: { key: `${K}nextConnectDomain` },
        actionKey: 'workspace.siteBar.connectDomainShort',
        bi: 'presence_next_step_connect_domain',
        chat: websiteChat('workspace.website.goLivePrompt', url, site),
      };
    }
  }
  const resources = settled(bindings);
  const social = resources
    .map((r, i) => ({ r, i }))
    .filter(({ r }) => r.kind === 'SOCIAL_PROFILE')
    .sort((a, b) => rank(a.r.provider) - rank(b.r.provider) || a.i - b.i)
    .map(({ r }) => r);
  const broken = social.find((r) => r.state === 'BROKEN');
  if (broken) {
    return {
      kind: 'RECONNECT',
      text: { key: `${K}nextReconnect`, values: { provider: linkProviderName(broken.provider) || rowName(broken) } },
      actionKey: `${R}actionReconnect`,
      bi: 'presence_next_step_reconnect',
      chat: resourceChat('RECONNECT', broken),
    };
  }
  const unverified = resources.find((r) => r.provider === 'GOOGLE_BUSINESS' && r.state === 'UNPUBLISHED');
  if (unverified) {
    return {
      kind: 'VERIFY',
      text: { key: `${K}nextVerify` },
      actionKey: `${K}actionVerify`,
      bi: 'presence_next_step_verify',
      chat: resourceChat('VERIFY', unverified),
    };
  }
  return null;
}

/* ------------------------------------------------------------------------------------- the Manage dialog */

export type ManageSectionId = 'suggestions' | 'websites' | 'social' | 'listings' | 'agents';

/** An inline action on a Manage row. Row-menu actions (Open, Make main, Move, Remove) stay in `menu`. */
export type ManageActionKind = 'CONFIRM' | 'DISMISS' | 'GO_LIVE' | 'UPGRADE' | 'CONNECT_DOMAIN' | 'RECONNECT' | 'CONNECT' | 'VERIFY' | 'CHANGE';

export interface ManageAction {
  kind: ManageActionKind;
  labelKey: string;
  /** Drawn as the row's lime (accent) button — only UPGRADE and CONFIRM; the rest are quiet. */
  primary: boolean;
  /** The page-click BI button name. */
  bi: string;
  /** What a chat action opens; absent on CONFIRM / DISMISS, which are UpdateSpaceBinding calls on `bindingId`. */
  chat?: PresenceChat;
}

/** A detail-line part: a catalog key, or display text as-is (a brand, a login, a domain). */
export type PresencePart = I18nCopy | { text: string };

export interface ManageRow {
  bindingId: string;
  kind: SpaceResourceKind;
  provider: string;
  glyph: PresenceGlyph;
  name: string;
  /** Websites only: the role badge — Main, Landing page, Design test or Other. */
  roleKey?: string;
  /** The grey line under the name, joined with " · ". */
  details: PresencePart[];
  /** The detail line reads as a warning (a broken login, a site not live, not owned, not verified). */
  tone?: StateBadgeTone;
  /** Agents section only: what the row is ("Business data", "Brand"), drawn as its label column. */
  labelKey?: string;
  actions: ManageAction[];
  /** The row menu (the Resources card's, in its order), without anything already inline. */
  menu: { action: SpaceResourceAction; key: string }[];
  openUrl: string;
  screenshotUrl: string;
}

export interface ManageSection {
  id: ManageSectionId;
  titleKey: string;
  rows: ManageRow[];
  /** The section's kind was declined and it has no rows: one quiet `workspace.resources.declined` line. */
  declinedKey?: string;
}

const PURPOSE_KEYS: Record<string, string> = {
  DESIGN_TEST: `${R}purposeDesignTest`,
  LANDING_PAGE: `${R}purposeLandingPage`,
  OTHER_PURPOSE: `${R}purposeOther`,
};

const TONES: Record<PresenceAttention, StateBadgeTone> = {
  NOT_LIVE: 'quiet',
  RECONNECT: 'warning',
  NOT_VERIFIED: 'warning',
  NOT_OWNED: 'critical',
};

function stateWords(r: SpaceResource, websiteRow: boolean): PresencePart | null {
  const attention = resourceAttention(r);
  if (attention) return { key: ATTENTION_KEYS[attention] };
  switch (r.state) {
    case 'CONNECTED':
      return { key: websiteRow ? `${K}stateLive` : 'workspace.siteBar.connected' };
    case 'LINK_ONLY':
      return { key: `${K}stateLinkOnly` };
    default:
      return { key: `${R}stateUnknown` };
  }
}

function brandPart(r: SpaceResource): PresencePart {
  const brand = linkProviderName(r.provider);
  return brand ? { text: brand } : { key: `${R}kindWebsite` };
}

function action(kind: ManageActionKind, labelKey: string, bi: string, chat?: PresenceChat, primary = false): ManageAction {
  return { kind, labelKey, primary, bi, ...(chat ? { chat } : {}) };
}

function baseRow(r: SpaceResource, kind: SpaceResourceKind): ManageRow {
  const row = spaceResourceRow(r, kind);
  return {
    bindingId: row.bindingId,
    kind,
    provider: row.provider,
    glyph: linkProviderGlyph(r.provider),
    name: rowName(r),
    details: [],
    actions: [],
    menu: row.menu,
    openUrl: presenceHref(row.openUrl),
    screenshotUrl: (r.screenshotUrl ?? '').trim(),
  };
}

/** The inline actions a resource's state calls for: Reconnect (BROKEN), Verify (unverified Google), Connect (LINK_ONLY + CONNECT offered). */
function stateActions(r: SpaceResource): ManageAction[] {
  const offered = new Set(r.actions ?? []);
  if (r.state === 'BROKEN') return [action('RECONNECT', `${R}actionReconnect`, 'presence_manage_reconnect', resourceChat('RECONNECT', r))];
  if (r.provider === 'GOOGLE_BUSINESS' && r.state === 'UNPUBLISHED') return [action('VERIFY', `${K}actionVerify`, 'presence_manage_verify', resourceChat('VERIFY', r))];
  if (r.state === 'LINK_ONLY' && offered.has('CONNECT')) return [action('CONNECT', `${K}actionConnect`, 'presence_manage_connect', resourceChat('CONNECT', r))];
  return [];
}

function withoutInline(row: ManageRow): ManageRow {
  const inline = new Set(row.actions.map((a) => a.kind));
  return { ...row, menu: row.menu.filter((m) => !inline.has(m.action as ManageActionKind)) };
}

function websiteRow(r: SpaceResource, site: WorkspaceWebsite | null, isTile: boolean): ManageRow {
  const row = baseRow(r, 'WEBSITE');
  const main = r.main === true || isTile;
  row.roleKey = main ? `${R}main` : PURPOSE_KEYS[r.purpose ?? ''];
  if (isTile && site) {
    // The tile decides the main site's state, plan and domain — and its Wix actions.
    const url = (site.url ?? '').trim();
    const live = site.published !== false;
    const domain = (site.customDomain ?? '').trim();
    const paid = sitePaid(site);
    row.name = row.name || websiteHost(url) || websiteDisplayName(site);
    row.openUrl = row.openUrl || (live ? presenceHref(url) : '');
    row.screenshotUrl = row.screenshotUrl || (site.screenshotUrl ?? '').trim();
    row.details = [brandPart(r), { key: live ? `${K}stateLive` : 'workspace.website.notLive' }];
    if (live) {
      row.details.push({ key: paid ? 'workspace.website.planPremium' : 'workspace.website.planFree' });
      row.details.push(domain ? { text: domain } : { key: `${K}wixAddress` });
    } else row.tone = 'quiet';
    if (!live) {
      const prompt = websitePublishPrompt(site);
      row.actions = [
        action('GO_LIVE', 'workspace.website.goLiveNow', 'presence_manage_go_live', {
          message: { key: prompt.key, values: prompt.values as Record<string, string> },
          context: { kind: 'website', website: site },
        }),
      ];
    } else {
      if (!paid) row.actions.push(action('UPGRADE', 'workspace.website.upgrade', 'presence_manage_upgrade', websiteChat('workspace.website.upgradePrompt', url, site), true));
      if (!domain) row.actions.push(action('CONNECT_DOMAIN', 'workspace.siteBar.connectDomainShort', 'presence_manage_connect_domain', websiteChat('workspace.website.goLivePrompt', url, site)));
    }
    return withoutInline(row);
  }
  const state = stateWords(r, true);
  row.details = [brandPart(r), ...(state ? [state] : [])];
  const attention = resourceAttention(r);
  if (attention) row.tone = TONES[attention];
  if (r.provider === 'WIX' && r.state === 'UNPUBLISHED') {
    // Another Wix site of the Space, not live yet: the same publish chat, about THAT site.
    const other: WorkspaceWebsite = { name: (r.displayName ?? '').trim(), url: presenceHref(r.openUrl), metaSiteId: (r.externalScopeId ?? '').trim() };
    const prompt = websitePublishPrompt(other);
    row.actions = [
      action('GO_LIVE', 'workspace.website.goLiveNow', 'presence_manage_go_live', {
        message: { key: prompt.key, values: prompt.values as Record<string, string> },
        context: { kind: 'website', website: other },
      }),
    ];
  } else row.actions = stateActions(r);
  return withoutInline(row);
}

function profileRow(r: SpaceResource): ManageRow {
  const row = baseRow(r, 'SOCIAL_PROFILE');
  const state = stateWords(r, false);
  const login = (r.loginLabel ?? '').trim();
  row.details = [brandPart(r), ...(state ? [state] : []), ...(login ? [{ key: `${R}via`, values: { login } }] : [])];
  const attention = resourceAttention(r);
  if (attention) row.tone = TONES[attention];
  row.actions = stateActions(r);
  return withoutInline(row);
}

function agentRow(r: SpaceResource, kind: 'BUSINESS_DATA' | 'BRAND'): ManageRow {
  const row = baseRow(r, kind);
  row.labelKey = kind === 'BRAND' ? `${R}kindBrand` : `${R}kindBusinessData`;
  const brand = linkProviderName(r.provider);
  row.details = brand ? [{ text: brand }] : [];
  const attention = resourceAttention(r);
  if (attention) {
    row.details.push({ key: ATTENTION_KEYS[attention] });
    row.tone = TONES[attention];
  }
  row.actions = [...stateActions(r), action('CHANGE', `${K}actionChange`, 'presence_manage_change', resourceChat('CHANGE', r))];
  return withoutInline(row);
}

/**
 * The Manage dialog's sections, in order — each left out when it has nothing to show:
 *   1. suggestions — every CANDIDATE row (any known kind), with Yes (CONFIRM) and Not this one (DISMISS);
 *   2. websites — main first (the tile's site carries its state, plan and domain, and Go live / Upgrade / Connect
 *      domain), then the rest with their role badge from `purpose`; a declined kind shows "None";
 *   3. social profiles — SOCIAL_PROFILE rows that are not listings, main first: brand · state · via {login}, with
 *      Reconnect when BROKEN and Connect when LINK_ONLY (and the server offers CONNECT);
 *   4. listings and reviews — GOOGLE_BUSINESS and YELP profiles, Verify on an unverified Google listing;
 *   5. what agents use — BUSINESS_DATA then BRAND rows, each with Change.
 * When the tile has a site and no WEBSITE row stands for it, the websites section leads with the tile's site.
 */
export function manageSections(bindings: SpaceBindingsResponse | undefined, website?: WorkspaceWebsite | null): ManageSection[] {
  const site = website ?? null;
  const resources = settled(bindings);
  const declined = new Set(bindings?.declinedKinds ?? []);
  const sections: ManageSection[] = [];

  const suggestions = presenceCandidates(bindings).map<ManageRow>((c) => ({
    bindingId: c.bindingId,
    kind: c.kind,
    provider: c.provider,
    glyph: c.glyph,
    name: c.name,
    details: [brandPart({ provider: c.provider })],
    actions: [
      action('CONFIRM', 'workspace.siteBar.confirmYes', 'presence_confirm_site', undefined, true),
      action('DISMISS', 'workspace.siteBar.notThisOne', 'presence_dismiss_site'),
    ],
    menu: [],
    openUrl: c.openUrl,
    screenshotUrl: '',
  }));
  if (suggestions.length) sections.push({ id: 'suggestions', titleKey: `${K}sectionSuggestions`, rows: suggestions });

  const standIn = tileRow(site, resources);
  const sites = mainFirst(resources.filter((r) => r.kind === 'WEBSITE'));
  const websiteRows = sites.map((r) => websiteRow(r, site, r === standIn));
  if (site && !standIn) websiteRows.unshift(websiteRow({ kind: 'WEBSITE', provider: 'WIX', main: true, displayName: site.name, openUrl: site.url }, site, true));
  if (websiteRows.length) sections.push({ id: 'websites', titleKey: `${K}sectionWebsites`, rows: websiteRows });
  else if (declined.has('WEBSITE')) sections.push({ id: 'websites', titleKey: `${K}sectionWebsites`, rows: [], declinedKey: `${R}declined` });

  const profiles = mainFirst(resources.filter((r) => r.kind === 'SOCIAL_PROFILE'));
  const social = profiles.filter((r) => !LISTING_PROVIDERS.has(r.provider ?? '')).map(profileRow);
  if (social.length) sections.push({ id: 'social', titleKey: `${R}kindSocialProfile`, rows: social });
  else if (declined.has('SOCIAL_PROFILE')) sections.push({ id: 'social', titleKey: `${R}kindSocialProfile`, rows: [], declinedKey: `${R}declined` });

  const listings = profiles.filter((r) => LISTING_PROVIDERS.has(r.provider ?? '')).map(profileRow);
  if (listings.length) sections.push({ id: 'listings', titleKey: `${K}sectionListings`, rows: listings });

  const agents = [
    ...mainFirst(resources.filter((r) => r.kind === 'BUSINESS_DATA')).map((r) => agentRow(r, 'BUSINESS_DATA')),
    ...mainFirst(resources.filter((r) => r.kind === 'BRAND')).map((r) => agentRow(r, 'BRAND')),
  ];
  if (agents.length) sections.push({ id: 'agents', titleKey: `${K}sectionAgents`, rows: agents });

  return sections;
}

/* ------------------------------------------------------------------------------------------- add a link */

/**
 * What "+ Add website, page or listing" sends as ADD_LINK's `url`: the pasted text trimmed, with `https://` added when
 * it has no scheme, or '' when it is not an http(s) address with a dotted host (the input then says so and sends
 * nothing). The server derives the provider and kind from it.
 */
export function presenceAddLinkUrl(input: string): string {
  const raw = input.trim();
  if (!raw || /\s/.test(raw)) return '';
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`;
  try {
    const parsed = new URL(withScheme);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return '';
    if (!parsed.hostname.includes('.') || parsed.hostname.startsWith('.') || parsed.hostname.endsWith('.')) return '';
    return withScheme;
  } catch {
    return '';
  }
}
